import React, { useState } from 'react';
import { Plus, Filter, History, TrendingUp, Calendar } from 'lucide-react';
import { useInventory } from '../context/InventoryContext';
import TransactionList from '../components/transactions/TransactionList';
import TransactionForm from '../components/transactions/TransactionForm';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

const TransactionsPage: React.FC = () => {
  const { transactions, products } = useInventory();
  
  const [showForm, setShowForm] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  
  // Filter transactions based on filters
  const filteredTransactions = transactions.filter(transaction => {
    const matchesType = transactionTypeFilter === '' || transaction.type === transactionTypeFilter;
    const matchesProduct = productFilter === '' || transaction.productId === productFilter;
    
    return matchesType && matchesProduct;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  const typeOptions = [
    { value: '', label: 'All Transaction Types' },
    { value: 'in', label: 'Stock In' },
    { value: 'out', label: 'Stock Out' },
    { value: 'adjustment', label: 'Adjustments' },
  ];
  
  const productOptions = [
    { value: '', label: 'All Products' },
    ...products.map(product => ({
      value: product.id,
      label: product.name,
    })),
  ];
  
  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Financial transaction records and accounting ledger with calculator"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Transactions</h1>
              <p className="text-lg opacity-90">
                Complete transaction history and inventory movement tracking
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <History className="h-4 w-4 mr-2" />
                  <span>{transactions.length} Total Transactions</span>
                </div>
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  <span>Real-time Updates</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Last Updated: {new Date().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4 flex gap-2">
            <Button 
              variant="secondary" 
              onClick={() => setIsFiltersVisible(!isFiltersVisible)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Transaction
            </Button>
          </div>
        </div>
      </div>
      
      {isFiltersVisible && (
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Select
              label="Transaction Type"
              options={typeOptions}
              value={transactionTypeFilter}
              onChange={(value) => setTransactionTypeFilter(value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Product"
              options={productOptions}
              value={productFilter}
              onChange={(value) => setProductFilter(value)}
              fullWidth
            />
          </div>
        </div>
      )}
      
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <TransactionForm onClose={() => setShowForm(false)} />
          </div>
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          <TransactionList transactions={filteredTransactions} />
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionsPage;